import React, { useState } from "react";
import "./WeatherDashboard.css";

function WeatherDashboard() {
  // State for city input, weather data, and error handling
  const [city, setCity] = useState("");
  const [weather, setWeather] = useState(null);
  const [error, setError] = useState("");

  // Replace with your Weatherbit API key
  const apiKey = "98b9fe5e8f554958a02eca1f6dfbd98d";

  // Fetch current weather data
  const fetchWeather = async () => {
    if (!city) return;
    try {
      setError("");
      const encodedCity = encodeURIComponent(city);
      const response = await fetch(
        `https://api.weatherbit.io/v2.0/current?city=${encodedCity}&key=${apiKey}&units=M`,
      );
      if (!response.ok) {
        throw new Error("City not found");
      }
      const json = await response.json();
      if (json.data && json.data.length > 0) {
        setWeather(json.data[0]);
      } else {
        throw new Error("No weather data available");
      }
    } catch (err) {
      setError(err.message);
      setWeather(null);
    }
  };

  // Allow pressing Enter in the input to trigger a search
  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      fetchWeather();
    }
  };

  // Helper function to safely retrieve a field or show "N/A"
  const safe = (val) => (val !== undefined && val !== null ? val : "N/A");

  return (
    <div className="dashboard-container">
      <h1 className="dashboard-title">Weather Vista</h1>
      <div className="search-container">
        <input
          type="text"
          value={city}
          onChange={(e) => setCity(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Enter city"
          className="city-input"
        />
        <button onClick={fetchWeather} className="search-button">
          Get Weather
        </button>
      </div>
      {error && <p className="error-message">{error}</p>}

      {weather && (
        <div className="weather-grid">
          {/* MAIN (LARGE) BLOCK */}
          <div className="block main-block">
            <h2>
              {safe(weather.city_name)}, {safe(weather.country_code)}
            </h2>
            <p className="temperature">{Math.round(safe(weather.temp))}°C</p>
            <p className="description">{safe(weather.weather?.description)}</p>
            <img
              src={`https://www.weatherbit.io/static/img/icons/${safe(weather.weather?.icon)}.png`}
              alt="Weather icon"
              className="weather-icon"
            />
          </div>

          {/* Sunrise & Sunset near the top */}
          <div className="block blockSunrise">
            <h3>Sunrise</h3>
            <p>{safe(weather.sunrise)}</p>
          </div>
          <div className="block blockSunset">
            <h3>Sunset</h3>
            <p>{safe(weather.sunset)}</p>
          </div>

          {/* Row 2: Humidity, Wind, Pressure */}
          <div className="block blockHumidity">
            <h3>Humidity</h3>
            <p>{safe(weather.rh)}%</p>
          </div>
          <div className="block blockWind">
            <h3>Wind</h3>
            <p>
              {safe(weather.wind_spd)} m/s
              {weather.wind_cdir_full ? `, ${weather.wind_cdir_full}` : ""}
            </p>
          </div>
          <div className="block blockPressure">
            <h3>Pressure</h3>
            <p>{safe(weather.pres)} hPa</p>
          </div>

          {/* Row 3: Feels Like, Dew Point, UV, Clouds, Visibility, AQI */}
          <div className="block blockFeelsLike">
            <h3>Feels Like</h3>
            <p>{Math.round(safe(weather.app_temp))}°C</p>
          </div>
          <div className="block blockDewPoint">
            <h3>Dew Point</h3>
            <p>{Math.round(safe(weather.dewpt))}°C</p>
          </div>
          <div className="block blockUV">
            <h3>UV Index</h3>
            <p>{safe(weather.uv)}</p>
          </div>
          <div className="block blockClouds">
            <h3>Cloud Coverage</h3>
            <p>{safe(weather.clouds)}%</p>
          </div>
          <div className="block blockVisibility">
            <h3>Visibility</h3>
            <p>{safe(weather.vis)} km</p>
          </div>
          <div className="block blockAQI">
            <h3>Air Quality (AQI)</h3>
            <p>{safe(weather.aqi)}</p>
          </div>

          {/* Row 4: Solar Rad, Precip, Snow, Observation Time, Timezone, Station */}
          <div className="block blockSolarRad">
            <h3>Solar Radiation</h3>
            <p>{safe(weather.solar_rad)} W/m²</p>
          </div>
          <div className="block blockPrecip">
            <h3>Precipitation</h3>
            <p>{safe(weather.precip)} mm/hr</p>
          </div>
          <div className="block blockSnow">
            <h3>Snow</h3>
            <p>{safe(weather.snow)} mm/hr</p>
          </div>
          <div className="block blockObTime">
            <h3>Observation Time</h3>
            <p>{safe(weather.ob_time)}</p>
          </div>
          <div className="block blockTimezone">
            <h3>Timezone</h3>
            <p>{safe(weather.timezone)}</p>
          </div>
          <div className="block blockStation">
            <h3>Station</h3>
            <p>{safe(weather.station)}</p>
          </div>
        </div>
      )}
    </div>
  );
}

export default WeatherDashboard;
